// Discord-compatible API wrapper
import { isRunningInDiscord, hasDiscordFeatures, getDiscordSDK } from './discordUtils';

// Check if running in Discord
const isDiscordEnvironment = () => {
  return isRunningInDiscord();
};

// Discord-compatible fetch function
export const discordFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  if (!isDiscordEnvironment()) {
    // Not in Discord, use regular fetch
    return fetch(url, options);
  }

  try {
    console.log('🌐 Discord environment detected, making API call:', url);
    
    // Debug Discord SDK state
    const discordSdk = getDiscordSDK();
    const hasFeatures = hasDiscordFeatures();
    console.log('🔍 Discord SDK Debug:', {
      hasDiscordFeatures: hasFeatures,
      discordSdkExists: !!discordSdk,
      isRunningInDiscord: isRunningInDiscord()
    });
    
    // For Discord Activities, we need to use the Discord SDK's fetch method
    // or configure URL mappings in the Discord Developer Portal
    // Check if Discord SDK is properly initialized using the correct function
    if (hasFeatures && discordSdk) {
      console.log('🔗 Using Discord SDK for API call');
      
      // Try to use Discord's proxy system
      // Discord Activities can make requests through Discord's proxy
      // The URL needs to be mapped in Discord Developer Portal
      
      // For now, try the direct approach with proper headers
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': 'Discord-Activity',
          ...options.headers,
        },
        mode: 'cors',
        credentials: 'omit', // Don't send credentials in Discord environment
      });
      
      console.log('✅ Discord API call successful:', response.status);
      return response;
    }
    
    // Discord SDK not fully available - CSP will block direct calls
    console.log('⚠️ Discord SDK not fully available, CSP will block direct API calls');
    console.log('🔄 Attempting workaround using Discord proxy domain...');
    
    // Try to construct a Discord proxy URL
    // Discord Activities get a unique subdomain like: https://1406709741341900921.discordsays.com/
    const urlParams = new URLSearchParams(window.location.search);
    const clientId = process.env.REACT_APP_DISCORD_CLIENT_ID || urlParams.get('client_id');
    
    if (clientId) {
      // Try to use Discord's proxy domain
      const discordProxyBase = `https://${clientId}.discordsays.com`;
      
      // Convert external URL to proxy path
      // https://ai-guessing-game-production.up.railway.app/api/text/game/random-texts
      // becomes: https://1406709741341900921.discordsays.com/api/text/game/random-texts
      const urlObj = new URL(url);
      const proxyUrl = `${discordProxyBase}${urlObj.pathname}${urlObj.search}`;
      
      console.log('🔗 Trying Discord proxy URL:', proxyUrl);
      
      try {
        const response = await fetch(proxyUrl, {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            'User-Agent': 'Discord-Activity',
            ...options.headers,
          },
          mode: 'cors',
          credentials: 'omit',
        });
        
        console.log('🔍 Discord proxy response:', {
          status: response.status,
          statusText: response.statusText,
          contentType: response.headers.get('content-type'),
          url: proxyUrl
        });
        
        // Check if the response is actually JSON and not an HTML error page
        const contentType = response.headers.get('content-type');
        if (response.ok && contentType && contentType.includes('application/json')) {
          console.log('✅ Discord proxy call successful with JSON response');
          return response;
        } else {
          // Response is not JSON (likely HTML error page)
          const responseText = await response.text();
          console.log('❌ Discord proxy returned non-JSON response:', {
            status: response.status,
            contentType: contentType,
            responsePreview: responseText.substring(0, 200) + '...'
          });
          throw new Error(`Discord proxy returned ${response.status} with non-JSON content`);
        }
      } catch (proxyError) {
        console.log('❌ Discord proxy failed:', {
          error: proxyError,
          message: proxyError instanceof Error ? proxyError.message : String(proxyError),
          proxyUrl: proxyUrl
        });
        // Fall through to error handling
      }
    }
    
    // If all else fails, throw a descriptive error
    throw new Error(`Discord CSP blocks direct API calls. Please configure URL mappings in Discord Developer Portal for: ${url}`);
    
  } catch (error) {
    console.error('❌ Discord API call failed:', {
      url,
      error: error,
      message: error instanceof Error ? error.message : String(error),
      solution: 'Configure URL mappings in Discord Developer Portal or use Discord proxy domain'
    });
    throw error;
  }
};